// Signature size presets. Every value is a plain number of pixels so it can be
// written as both an HTML attribute (required by Outlook) and an inline style.
export const SIGNATURE_SIZE_PRESETS = {
  compact: {
    label: 'Compact',
    description: 'Roughly 25% smaller - best for teams that reply a lot',
    maxWidth: 480,
    photoSize: 72,
    mediaColumn: 88,
    logoMax: 88,
    columnGap: 12,
    nameSize: 16,
    titleSize: 13,
    bodySize: 12,
    socialIcon: 20,
    socialGlyph: 11,
    disclaimerSize: 8,
    bannerSizes: { small: 200, medium: 300, large: 400 },
    bannerWidth: 300,
    rowGap: 0,
    blockGap: 10,
  },
  standard: {
    label: 'Standard',
    description: 'The default size',
    maxWidth: 600,
    photoSize: 100,
    mediaColumn: 120,
    logoMax: 120,
    columnGap: 15,
    nameSize: 20,
    titleSize: 16,
    bodySize: 14,
    socialIcon: 24,
    socialGlyph: 14,
    disclaimerSize: 9,
    bannerSizes: { small: 260, medium: 400, large: 520 },
    bannerWidth: 400,
    rowGap: 1,
    blockGap: 15,
  },
  large: {
    label: 'Large',
    description: 'Bigger type and imagery for presentation-heavy teams',
    maxWidth: 680,
    photoSize: 120,
    mediaColumn: 140,
    logoMax: 140,
    columnGap: 18,
    nameSize: 24,
    titleSize: 18,
    bodySize: 16,
    socialIcon: 28,
    socialGlyph: 16,
    disclaimerSize: 10,
    bannerSizes: { small: 320, medium: 500, large: 650 },
    bannerWidth: 500,
    rowGap: 2,
    blockGap: 18,
  },
};

export const SIGNATURE_SIZE_OPTIONS = Object.entries(SIGNATURE_SIZE_PRESETS).map(([value, preset]) => ({
  value,
  label: preset.label,
  description: preset.description,
}));

export const BANNER_SIZE_OPTIONS = [
  { value: 'small', label: 'Small' },
  { value: 'medium', label: 'Medium' },
  { value: 'large', label: 'Large' },
];

export const DEFAULT_BANNER_SIZE = 'medium';

export const BANNER_WIDTH_MIN = 120;
export const BANNER_WIDTH_MAX = 600;

/** Preset banner width in px for a signature size + banner size pair. */
export const getBannerPresetWidth = (size, bannerSize) => {
  const preset = SIGNATURE_SIZE_PRESETS[size] || SIGNATURE_SIZE_PRESETS.standard;
  return preset.bannerSizes[bannerSize] || preset.bannerSizes[DEFAULT_BANNER_SIZE];
};

/**
 * Resolve the size tokens for a signature.
 * @param size one of compact | standard | large
 * @param overrides optional overrides, e.g. { bannerSize: 'small', bannerWidth: 220, photoSize: 80 }
 */
export const getSizeTokens = (size, overrides = {}) => {
  const preset = SIGNATURE_SIZE_PRESETS[size] || SIGNATURE_SIZE_PRESETS.standard;
  const { bannerSize, ...numericOverrides } = overrides;
  const tokens = { ...preset };

  tokens.bannerWidth = getBannerPresetWidth(size, bannerSize);

  Object.entries(numericOverrides).forEach(([key, value]) => {
    const numeric = Number(value);
    if (value !== null && value !== undefined && value !== '' && !Number.isNaN(numeric) && numeric > 0) {
      tokens[key] = numeric;
    }
  });

  tokens.bannerWidth = Math.min(Math.max(tokens.bannerWidth, BANNER_WIDTH_MIN), tokens.maxWidth);
  return tokens;
};

export default getSizeTokens;
